import React from 'react';
import { buildPurchaseRewardPreview, formatShareCount } from '../lib/purchaseRewardPreview.js';

type PackageId = 'P1' | 'P2' | 'P3' | 'P4' | 'P5';

interface PurchaseRewardPreviewProps {
  productionCapitalPackages?: Array<{ packageId: PackageId; qty: number }>;
  productionCapitalPackage?: PackageId;
  productionCapitalPackageQty?: number;
  birthdayPackage?: 'BIRTHDAY_2' | 'BIRTHDAY_25';
  birthdayQty?: number;
  phasePrice?: number;
  paidShares?: number;
  tierBonusShares?: number;
  singleShareActive?: boolean;
}

/**
 * Shows the packages, buyer bonus, and sponsor bonus already chosen.
 * Display only. It does not change the amount due or the shares submitted.
 */
const PurchaseRewardPreview: React.FC<PurchaseRewardPreviewProps> = ({
  productionCapitalPackages,
  productionCapitalPackage,
  productionCapitalPackageQty,
  birthdayPackage,
  birthdayQty,
  phasePrice,
  paidShares,
  tierBonusShares,
  singleShareActive = false,
}) => {
  const packages = productionCapitalPackages && productionCapitalPackages.length
    ? productionCapitalPackages
    : productionCapitalPackage
      ? [{ packageId: productionCapitalPackage, qty: productionCapitalPackageQty || 1 }]
      : [];

  const preview = buildPurchaseRewardPreview({
    packages,
    phasePrice,
    birthdayPackage,
    birthdayQty,
    paidShares,
    tierBonusShares: packages.length || birthdayPackage ? 0 : tierBonusShares,
    singleShareActive: packages.length || birthdayPackage ? false : singleShareActive,
  });

  if (!preview) return null;

  return (
    <div
      data-testid="purchase-reward-preview"
      style={{
        marginTop: '12px',
        padding: '12px',
        borderRadius: '8px',
        background: 'rgba(212, 175, 55, 0.08)',
        border: '1px solid rgba(212, 175, 55, 0.35)',
        textAlign: 'left',
      }}
    >
      {preview.packages.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ color: '#FFD700', fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>Packages you chose</div>
          {preview.packages.map((row) => (
            <div key={row.label} style={{ marginBottom: '8px', padding: '8px', borderRadius: '8px', background: 'rgba(0,0,0,0.35)' }}>
              <div style={{ color: '#FFD700', fontWeight: 700, marginBottom: '4px' }}>{row.label}</div>
              <p style={{ margin: '0 0 2px 0' }}>Paid shares: {formatShareCount(row.paidShares)}</p>
              <p style={{ margin: '0 0 2px 0' }}>Your bonus shares: <strong style={{ color: '#10b981' }}>+{formatShareCount(row.buyerBonus)}</strong></p>
              <p style={{ margin: 0 }}>Your sponsor receives: <strong style={{ color: '#10b981' }}>+{formatShareCount(row.sponsorBonus)}</strong></p>
            </div>
          ))}
        </div>
      )}
      <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(212, 175, 55, 0.35)' }}>
        <div style={{ color: '#FFD700', fontWeight: 700, marginBottom: '4px' }}>Order total</div>
        <p style={{ margin: '0 0 4px 0' }}>
          Your bonus shares: <strong style={{ color: '#10b981' }}>+{formatShareCount(preview.buyerBonus)}</strong>
        </p>
        <p style={{ margin: '0 0 4px 0' }}>
          You receive: <strong>{formatShareCount(preview.buyerTotal)}</strong> shares
        </p>
        {preview.sponsorBonus != null && (
          <p style={{ margin: 0 }}>
            Your sponsor receives: <strong style={{ color: '#10b981' }}>+{formatShareCount(preview.sponsorBonus)}</strong> bonus shares
          </p>
        )}
      </div>
    </div>
  );
};

export default PurchaseRewardPreview;
